import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, CheckIcon, AlertTriangleIcon } from 'lucide-react';
const CreateTicketingGroup = () => {
  const {
    groupId
  } = useParams();
  const navigate = useNavigate();
  const isEditing = !!groupId;
  // Form state
  const [formData, setFormData] = useState({
    issueVoucherNo: '',
    requestRef: '',
    tourCode: '',
    deptTeam: '',
    receivingStaff: '',
    relTourCode: '',
    contact: '',
    remarks: '',
    issueDate: new Date().toISOString().split('T')[0],
    issueStaff: 'John Doe',
    completeId: '',
    cancelStaff: '',
    cancelDate: ''
  });
  const [errors, setErrors] = useState({
    issueVoucherNo: '',
    requestRef: '',
    tourCode: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  // Load data if editing
  useEffect(() => {
    if (isEditing) {
      // In a real app, you would fetch the data from an API
      // For this demo, we'll simulate loading data
      setTimeout(() => {
        setFormData({
          issueVoucherNo: groupId,
          requestRef: 'REF-001-2023',
          tourCode: 'HKGTOUR23',
          deptTeam: 'DB',
          receivingStaff: 'Jane Doe',
          relTourCode: '12XCAI1207/23QR',
          contact: '555-1234',
          remarks: 'VIP group booking',
          issueDate: '2023-06-15',
          issueStaff: 'John Doe',
          completeId: 'COMP-2023-001',
          cancelStaff: '',
          cancelDate: ''
        });
      }, 300);
    }
  }, [isEditing, groupId]);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const handleSubmit = e => {
    e.preventDefault();
    // Validate form
    const newErrors = {};
    if (!formData.issueVoucherNo.trim()) {
      newErrors.issueVoucherNo = 'Issue Voucher No is required';
    }
    if (!formData.requestRef.trim()) {
      newErrors.requestRef = 'Request Ref is required';
    }
    if (!formData.tourCode.trim()) {
      newErrors.tourCode = 'Tour Code is required';
    } else if (!/^[a-zA-Z0-9]+$/.test(formData.tourCode)) {
      newErrors.tourCode = 'Tour Code must be alphanumeric';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    // Form is valid, submit data
    setFormStatus('saving');
    // Simulate API call
    setTimeout(() => {
      console.log('Form submitted:', formData);
      setFormStatus('success');
      // After successful save, navigate to ticket management
      setTimeout(() => {
        navigate(isEditing ? `/tickets/${formData.issueVoucherNo}` : `/tickets/${formData.issueVoucherNo}`);
      }, 1000);
    }, 800);
  };
  const renderFormStatusMessage = () => {
    if (formStatus === 'saving') {
      return <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6" data-id="element-1380">
          <div className="flex" data-id="element-1381">
            <div className="flex-shrink-0" data-id="element-1382">
              <svg className="animate-spin h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1383">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1384"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1385"></path>
              </svg>
            </div>
            <div className="ml-3" data-id="element-1386">
              <p className="text-sm text-blue-700" data-id="element-1387">Saving your changes...</p>
            </div>
          </div>
        </div>;
    }
    if (formStatus === 'success') {
      return <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6" data-id="element-1388">
          <div className="flex" data-id="element-1389">
            <div className="flex-shrink-0" data-id="element-1390">
              <CheckIcon className="h-5 w-5 text-green-400" aria-hidden="true" data-id="element-1391" />
            </div>
            <div className="ml-3" data-id="element-1392">
              <p className="text-sm text-green-700" data-id="element-1393">
                Ticket group saved successfully! Redirecting to ticket
                management...
              </p>
            </div>
          </div>
        </div>;
    }
    if (formStatus === 'error') {
      return <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6" data-id="element-1394">
          <div className="flex" data-id="element-1395">
            <div className="flex-shrink-0" data-id="element-1396">
              <AlertTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" data-id="element-1397" />
            </div>
            <div className="ml-3" data-id="element-1398">
              <p className="text-sm text-red-700" data-id="element-1399">
                There was an error saving your changes. Please try again.
              </p>
            </div>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="space-y-6" data-id="element-1400">
      <div className="flex items-center gap-4" data-id="element-1401">
        <Link to="/" className="text-gray-500 hover:text-gray-700" data-id="element-1402">
          <ArrowLeftIcon className="h-5 w-5" data-id="element-1403" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1404">
          {isEditing ? 'Edit Ticket Group' : 'Create New Ticket Group'}
        </h1>
      </div>
      {renderFormStatusMessage()}
      <div className="bg-white shadow rounded-lg" data-id="element-1405">
        <form onSubmit={handleSubmit} className="p-6" data-id="element-1406">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1407">
            <div data-id="element-1408">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1409">
                Issue Voucher No <span className="text-red-500" data-id="element-1410">*</span>
              </label>
              <input type="text" name="issueVoucherNo" value={formData.issueVoucherNo} onChange={handleChange} disabled={isEditing} className={`mt-1 block w-full px-3 py-2 border ${errors.issueVoucherNo ? 'border-red-300 ring-red-500' : 'border-gray-300'} ${isEditing ? 'bg-gray-100' : ''} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} placeholder="e.g. GS113824" data-id="element-1411" />
              {errors.issueVoucherNo && <p className="mt-1 text-sm text-red-600" data-id="element-1412">
                  {errors.issueVoucherNo}
                </p>}
            </div>
            <div data-id="element-1413">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1414">
                Issue Date
              </label>
              <input type="date" name="issueDate" value={formData.issueDate} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1415" />
            </div>
            <div data-id="element-1416">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1417">
                Request Ref <span className="text-red-500" data-id="element-1418">*</span>
              </label>
              <input type="text" name="requestRef" value={formData.requestRef} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.requestRef ? 'border-red-300 ring-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} placeholder="e.g. 12XCAI1207" data-id="element-1419" />
              {errors.requestRef && <p className="mt-1 text-sm text-red-600" data-id="element-1420">{errors.requestRef}</p>}
            </div>
            <div data-id="element-1421">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1422">
                Issue Staff
              </label>
              <input type="text" name="issueStaff" value={formData.issueStaff} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1423" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-1424">
                Defaults to current user, can be changed if needed
              </p>
            </div>
            <div data-id="element-1425">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1426">
                Dept/Team
              </label>
              <select name="deptTeam" value={formData.deptTeam} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1427">
                <option value="" data-id="element-1428">Select Department</option>
                <option value="DB" data-id="element-1429">DB - Database</option>
                <option value="IT" data-id="element-1430">IT - Information Technology</option>
                <option value="FIN" data-id="element-1431">FIN - Finance</option>
                <option value="HR" data-id="element-1432">HR - Human Resources</option>
                <option value="OPS" data-id="element-1433">OPS - Operations</option>
              </select>
            </div>
            <div data-id="element-1434">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1435">
                Complete ID
              </label>
              <input type="text" name="completeId" value={formData.completeId} disabled className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm text-gray-500 sm:text-sm" data-id="element-1436" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-1437">
                Auto-generated upon completion
              </p>
            </div>
            <div data-id="element-1438">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1439">
                Receiving Staff
              </label>
              <input type="text" name="receivingStaff" value={formData.receivingStaff} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1440" />
            </div>
            <div data-id="element-1441">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1442">
                Cancel Staff
              </label>
              <input type="text" name="cancelStaff" value={formData.cancelStaff} disabled className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm text-gray-500 sm:text-sm" data-id="element-1443" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-1444">
                Only applicable if ticket group is cancelled
              </p>
            </div>
            <div data-id="element-1445">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1446">
                Rel Tour Code
              </label>
              <input type="text" name="relTourCode" value={formData.relTourCode} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g. 12XCAI1207/23QR" data-id="element-1447" />
            </div>
            <div data-id="element-1448">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1449">
                Cancel Date
              </label>
              <input type="date" name="cancelDate" value={formData.cancelDate} disabled className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm text-gray-500 sm:text-sm" data-id="element-1450" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-1451">
                Only applicable if ticket group is cancelled
              </p>
            </div>
            <div data-id="element-1452">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1453">
                Contact
              </label>
              <input type="text" name="contact" value={formData.contact} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g. Phone number or email" data-id="element-1454" />
            </div>
            <div data-id="element-1455">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1456">
                Tour Code <span className="text-red-500" data-id="element-1457">*</span>
              </label>
              <input type="text" name="tourCode" value={formData.tourCode} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.tourCode ? 'border-red-300 ring-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} data-id="element-1458" />
              {errors.tourCode && <p className="mt-1 text-sm text-red-600" data-id="element-1459">{errors.tourCode}</p>}
            </div>
            <div className="md:col-span-2" data-id="element-1460">
              <label className="block text-sm font-medium text-gray-700" data-id="element-1461">
                Remarks
              </label>
              <textarea name="remarks" value={formData.remarks} onChange={handleChange} rows={3} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Additional notes or special instructions" data-id="element-1462" />
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-3" data-id="element-1463">
            <Link to="/" className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-1464">
              Cancel
            </Link>
            <button type="submit" disabled={formStatus === 'saving' || formStatus === 'success'} className={`inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${formStatus === 'saving' || formStatus === 'success' ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-1465">
              {formStatus === 'saving' ? <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1466">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1467"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1468"></path>
                  </svg>
                  Saving...
                </> : <>
                  <SaveIcon className="h-4 w-4 mr-1" data-id="element-1469" />
                  {isEditing ? 'Update' : 'Entry Complete'}
                </>}
            </button>
          </div>
        </form>
      </div>
    </div>;
};
export default CreateTicketingGroup;